// Stripe self-serve subscription state and actions.
//
// Separate from use-plan-change-requests: that is the request/approve flow for
// Enterprise and for downgrades and is unchanged by this. These cover the tiers
// that have a price in Stripe, which today is Scale alone.

import { useMutation, useQuery } from "@tanstack/react-query";
import { apiFetch, post } from "@/lib/api";
import { API_BASE } from "@/lib/utils";
import { keys } from "./keys";
import { useAuth } from "@/lib/auth";

export interface SubscriptionState {
  subscription: {
    plan: string;
    status: string;
    quantity: number;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
  } | null;
  connectedAccounts: number;
}

/**
 * Whether this user may read the org's billing position at all.
 *
 * The three GETs below are gated on owner/admin server-side, matching the
 * sibling reads in routes/billing.ts and the persona table's "developer cannot
 * manage billing". A developer still reaches the billing TAB — plan and usage
 * are theirs to see — so without this gate their page would fire three requests
 * that 403 and render error states for sections they simply do not have.
 * Frontend gate matches backend gate (CLAUDE.md, UX rule 6).
 */
export function useCanReadBilling(): boolean {
  const { userContext } = useAuth();
  const role = userContext?.role;
  return role === "owner" || role === "admin" || role === "platform_admin";
}

export function useSubscription() {
  // orgId is part of the key by the factory's own rule: without it an account
  // switch keeps serving the previous org's subscription from cache.
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  const canRead = useCanReadBilling();
  return useQuery<SubscriptionState>({
    queryKey: keys.billing.subscription(orgId),
    queryFn: () => apiFetch<SubscriptionState>(`${API_BASE}/v1/billing/subscription`),
    enabled: !!orgId && canRead,
    // Returning from Stripe lands back on this page, and the webhook that flips
    // the plan can arrive a beat after the redirect. Refetching on focus is what
    // stops a customer who has just paid from staring at "Launch".
    refetchOnWindowFocus: true,
  });
}

/**
 * Starts Stripe Checkout. Resolves by navigating away — the plan itself changes
 * only when the webhook reports the subscription active, never on return from
 * the success URL, which is just a redirect anyone could visit.
 */
export function useStartCheckout() {
  return useMutation({
    mutationFn: async (plan: string) => {
      const here = `${window.location.origin}${window.location.pathname}`;
      const { url } = await apiFetch<{ url: string }>(
        `${API_BASE}/v1/billing/checkout`,
        post({
          plan,
          successUrl: `${here}?checkout=success`,
          cancelUrl: `${here}?checkout=cancelled`,
        }),
      );
      window.location.href = url;
    },
  });
}

/** One invoice, as GET /api/v1/billing/invoices serves it. Amounts are CENTS. */
export interface BillingInvoice {
  id: string;
  number: string | null;
  created: string;
  amountDue: number;
  amountPaid: number;
  currency: string;
  status: string | null;
  hostedUrl: string | null;
  pdfUrl: string | null;
}

/**
 * The org's Stripe invoices.
 *
 * Served from Stripe rather than a local projection, so it is the same set of
 * figures as the receipt the customer was emailed. An org with no billing
 * account gets an empty list and not an error — that is every org on Launch.
 */
export function useInvoices() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  const canRead = useCanReadBilling();
  return useQuery<{ invoices: BillingInvoice[] }>({
    queryKey: keys.billing.invoices(orgId),
    queryFn: () => apiFetch<{ invoices: BillingInvoice[] }>(`${API_BASE}/v1/billing/invoices`),
    enabled: !!orgId && canRead,
    // Same reason as useSubscription: the first invoice is issued during
    // checkout, and the customer lands back here moments later.
    refetchOnWindowFocus: true,
  });
}

export interface PaymentMethodCard {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

/** The card on file — brand, last four and expiry only; the number never
 *  leaves Stripe. Null when there is no billing account or no default card. */
export function usePaymentMethod() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  const canRead = useCanReadBilling();
  return useQuery<{ paymentMethod: PaymentMethodCard | null }>({
    queryKey: keys.billing.paymentMethod(orgId),
    queryFn: () => apiFetch<{ paymentMethod: PaymentMethodCard | null }>(`${API_BASE}/v1/billing/payment-method`),
    enabled: !!orgId && canRead,
    refetchOnWindowFocus: true,
  });
}

/** Opens the Stripe-hosted portal: cards, invoices, cancellation. */
export function useOpenBillingPortal() {
  return useMutation({
    mutationFn: async () => {
      const { url } = await apiFetch<{ url: string }>(
        `${API_BASE}/v1/billing/portal`,
        post({ returnUrl: window.location.href }),
      );
      // No cache invalidation here: this is a hard navigation, so the whole
      // in-memory cache goes with the page and returning from Stripe is a fresh
      // load. Invalidating would fire one refetch whose response is thrown away.
      window.location.href = url;
    },
  });
}
